import Link from 'next/link';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Autoplay, EffectFade, Navigation, Pagination, Parallax } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/effect-fade';
import { useRef } from 'react';
import AnimatedEyebrow from '../AnimatedEyebrow';
import ImageOnlySkeletonLoader from '../ImageOnlySkeletonLoader';
import { useI18n } from '../../context/I18nContext';
import { getHomeData } from './data';

const AUTOPLAY_DELAY = 6500;
const AUTOPLAY_SECONDS = AUTOPLAY_DELAY / 1000;
const ENTER_PHASE = Math.min(1.1, AUTOPLAY_SECONDS * 0.2);
const ENTER_RATIO = ENTER_PHASE / AUTOPLAY_SECONDS;
const ENTER_DELAY_SOFT = 0.08;
const ENTER_DELAY_LEFT = 0.18;
const ENTER_DELAY_RIGHT = 0.28;

export default function HomeShowcaseSliderSection() {
  const { language, t } = useI18n();
  const { spotlightSlides } = getHomeData(language);
  const prevButtonRef = useRef(null);
  const nextButtonRef = useRef(null);
  const paginationRef = useRef(null);

  return (
    <section id="etc-slider" className="relative">
      <div className="">
        <div className="relative overflow-hidden h-screen">
          <Swiper
            modules={[Parallax, Navigation, Pagination, EffectFade, Autoplay]}
            className="home-showcase-swiper h-screen"
            speed={900}
            effect="fade"
            fadeEffect={{ crossFade: true }}
            spaceBetween={0}
            slidesPerView={1}
            navigation={{
              prevEl: prevButtonRef.current,
              nextEl: nextButtonRef.current,
            }}
            autoplay={{
              delay: AUTOPLAY_DELAY,
              disableOnInteraction: false,
              pauseOnMouseEnter: false,
            }}
            observer
            observeParents
            onBeforeInit={(swiper) => {
              swiper.params.navigation = {
                ...swiper.params.navigation,
                prevEl: prevButtonRef.current,
                nextEl: nextButtonRef.current,
              };
              swiper.params.pagination = {
                ...swiper.params.pagination,
                el: paginationRef.current,
              };
            }}
            onSwiper={() => {
              if (typeof window === 'undefined') return;
              window.dispatchEvent(new CustomEvent('hero-autoplay-progress', { detail: { progress: 0 } }));
            }}
            onAutoplayTimeLeft={(_, timeLeft) => {
              if (typeof window === 'undefined') return;
              const progress = ((AUTOPLAY_DELAY - timeLeft) / AUTOPLAY_DELAY) * 100;
              window.dispatchEvent(
                new CustomEvent('hero-autoplay-progress', {
                  detail: { progress: Math.max(0, Math.min(100, progress)) },
                }),
              );
            }}
            pagination={{
              el: paginationRef.current,
              clickable: true,
            }}
            parallax
            loop
            grabCursor
          >
            {spotlightSlides.map((slide, idx) => (
              <SwiperSlide key={slide.titleMain}>
                {({ isActive }) => (
                <div className="relative h-full w-full">
                  {slide.mediaType === 'video' ? (
                    <ImageOnlySkeletonLoader
                      mode="video"
                      videoPoster={slide.videoPoster || slide.image}
                      videoPosterSources={[
                        { minWidth: 0, src: '/slides/slide1-640.webp' },
                        { minWidth: 768, src: '/slides/slide1-1280.webp' },
                        { minWidth: 1440, src: '/slides/slide1-1920.webp' },
                      ]}
                      videoSources={[{ src: slide.videoSrc, type: 'video/mp4' }]}
                      containerClassName="absolute inset-0"
                      videoClassName="absolute inset-0 h-full w-full object-cover"
                    />
                  ) : (
                    <ImageOnlySkeletonLoader
                      src={slide.image}
                      imageSources={
                        slide.image === '/slides/slide2.webp'
                          ? [
                              { width: 640, src: '/slides/slide2-640.webp' },
                              { width: 1280, src: '/slides/slide2-1280.webp' },
                              { width: 1920, src: '/slides/slide2-1920.webp' },
                            ]
                          : []
                      }
                      mode="native"
                      alt={slide.titleMain}
                      loading={idx === 0 ? 'eager' : 'lazy'}
                      fetchPriority={idx === 0 ? 'high' : 'auto'}
                      containerClassName="absolute inset-0"
                      imgClassName="absolute inset-0 h-full w-full object-cover"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-br from-black/100 via-black/25 to-transparent" />
                  <div className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-48 bg-gradient-to-b from-black/100 to-transparent" />
                  <div className={`absolute inset-0 ${slide.overlayClass}`} />
                  <div className="pointer-events-none absolute inset-0 z-[2] overflow-hidden">
                    <motion.div
                      initial={false}
                      animate={
                        isActive
                          ? { x: [-280, 0, 18, 0], opacity: [0, 0.55, 0.62, 0.45] }
                          : { x: -280, opacity: 0 }
                      }
                      transition={
                        isActive
                          ? {
                              duration: AUTOPLAY_SECONDS,
                              times: [0, ENTER_RATIO, 0.72, 1],
                              ease: 'linear',
                              delay: ENTER_DELAY_SOFT,
                            }
                          : { duration: 0.7, ease: 'easeOut' }
                      }
                      className="absolute left-[2%] top-0 h-full w-[30%]"
                    >
                      <div
                        className="h-full w-full origin-left skew-x-[-10deg]"
                        style={{ backgroundColor: 'rgba(var(--dash-secondary-rgb), 0.75)' }}
                      />
                    </motion.div>
                    <motion.div
                      initial={false}
                      animate={
                        isActive
                          ? { x: [-220, 0, 22, 0], opacity: [0, 0.7, 0.8, 0.52] }
                          : { x: -220, opacity: 0 }
                      }
                      transition={
                        isActive
                          ? {
                              duration: AUTOPLAY_SECONDS,
                              times: [0, ENTER_RATIO, 0.74, 1],
                              ease: 'linear',
                              delay: ENTER_DELAY_LEFT,
                            }
                          : { duration: 0.7, ease: 'easeOut' }
                      }
                      className="absolute -left-[30%] top-0 h-full w-[40%]"
                    >
                      <div
                        className="h-full w-full origin-left skew-x-[-10deg]"
                        style={{ backgroundColor: 'rgba(var(--dash-main-rgb), 1)' }}
                      />
                    </motion.div>
                    <motion.div
                      initial={false}
                      animate={
                        isActive
                          ? { x: [220, 0, -22, 0], opacity: [0, 0.7, 0.8, 0.52] }
                          : { x: 220, opacity: 0 }
                      }
                      transition={
                        isActive
                          ? {
                              duration: AUTOPLAY_SECONDS,
                              times: [0, ENTER_RATIO, 0.74, 1],
                              ease: 'linear',
                              delay: ENTER_DELAY_RIGHT,
                            }
                          : { duration: 0.7, ease: 'easeOut' }
                      }
                      className="absolute -right-[40%] top-0 h-full w-[40%]"
                    >
                      <div
                        className="h-full w-full origin-left skew-x-[-10deg]"
                        style={{ backgroundColor: 'rgba(var(--dash-other-rgb), .8)' }}
                      />
                    </motion.div>
                    
                  </div>

                  <div className="container relative z-10 mx-auto flex h-full items-center px-6 md:px-12">
                    <div className="max-w-2xl text-white">
                      <div data-swiper-parallax="-220" data-swiper-parallax-opacity="0">
                        <AnimatedEyebrow className="mb-4">{slide.chip}</AnimatedEyebrow>
                      </div>
                      <h2
                        className="mt-4 text-4xl font-light uppercase leading-tight text-white md:text-6xl"
                        data-swiper-parallax="-360"
                        data-swiper-parallax-opacity="0"
                      >
                        <span className="block">{slide.titleAccent}</span>
                        <span className="block font-black">{slide.titleMain}</span>
                      </h2>
                      <p
                        className="mt-5 max-w-xl text-sm leading-relaxed text-white/90 md:text-base"
                        data-swiper-parallax="-260"
                        data-swiper-parallax-opacity="0"
                      >
                        {slide.description}
                      </p>
                      <div className="mt-8" data-swiper-parallax="-220" data-swiper-parallax-opacity="0">
                        <Link
                          href={slide.ctaHref}
                          className="group relative inline-flex items-center gap-3 overflow-hidden rounded-full bg-[var(--dash-main)] px-8 py-4 text-sm font-bold text-white transition-all active:scale-95 hover:pr-14 hover:bg-[var(--dash-other)] hover:text-[var(--dash-black)]"
                        >
                          <span className="relative z-10">{slide.ctaLabel}</span>
                          <ArrowRight className="absolute right-5 h-5 w-5 translate-x-8 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
                )}
              </SwiperSlide>
            ))}

            <div className="pointer-events-none absolute inset-0 z-20">
              <div className="container mx-auto flex h-full items-center justify-end px-4 sm:px-6 md:px-12">
                <div className="pointer-events-auto flex flex-col relative bottom-[-25%] gap-3">
                  <button
                    ref={prevButtonRef}
                    type="button"
                    aria-label={t('home.showcase.prevSlide')}
                    className="home-showcase-prev group relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-full"
                  >
                    <div className="absolute inset-0 scale-100 rounded-full bg-[var(--dash-main)] transition-all duration-700 group-hover:scale-150 group-hover:opacity-0" />
                    <div className="absolute inset-0 scale-0 rounded-full bg-[var(--dash-other)] transition-transform duration-500 group-hover:scale-100" />
                    <ChevronLeft className="relative z-10 h-6 w-6 text-white group-hover:text-[var(--dash-black)] transition-colors" />
                  </button>
                  <button
                    ref={nextButtonRef}
                    type="button"
                    aria-label={t('home.showcase.nextSlide')}
                    className="home-showcase-next group relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-full"
                  >
                    <div className="absolute inset-0 scale-100 rounded-full bg-[var(--dash-main)] transition-all duration-700 group-hover:scale-150 group-hover:opacity-0" />
                    <div className="absolute inset-0 scale-0 rounded-full bg-[var(--dash-other)] transition-transform duration-500 group-hover:scale-100" />
                    <ChevronRight className="relative z-10 h-6 w-6 text-white group-hover:text-[var(--dash-black)] transition-colors" />
                  </button>
                </div>
              </div>
            </div>

            <div className="pointer-events-none absolute inset-x-0 bottom-10 z-20">
              <div className="container mx-auto flex justify-center px-6 md:px-12">
                <div ref={paginationRef} className="home-showcase-pagination pointer-events-auto swiper-pagination !static !w-auto" />
              </div>
            </div>
          </Swiper>
        </div>
      </div>
    </section>
  );
}
